import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, MagnifyingGlass } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Window32PrimeAnalysis() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [affinity, setAffinity] = useState(null);
  const [affinityLoading, setAffinityLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAll = useCallback((silent = false) => {
    if (!silent) { setLoading(true); setAffinityLoading(true); }
    axios.get(`${API}/analysis/prime-analysis`)
      .then(res => setData(res.data))
      .catch(() => { if (!silent) toast.error('Failed to fetch prime analysis'); })
      .finally(() => { if (!silent) setLoading(false); });
    axios.get(`${API}/analysis/prime-affinity`)
      .then(res => setAffinity(res.data))
      .catch(() => setAffinity(null))
      .finally(() => { if (!silent) setAffinityLoading(false); });
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-update
  useEffect(() => {
    const onEvt = () => fetchAll(true);
    const onStorage = (e) => { if (e.key === 'euromillions:last_draw_update') fetchAll(true); };
    window.addEventListener('euromillions:draws-updated', onEvt);
    window.addEventListener('storage', onStorage);
    let bc;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('euromillions');
        bc.onmessage = (ev) => { if (ev.data === 'draws-updated') fetchAll(true); };
      }
    } catch {}
    return () => {
      window.removeEventListener('euromillions:draws-updated', onEvt);
      window.removeEventListener('storage', onStorage);
      if (bc) bc.close();
    };
  }, [fetchAll]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 32</h1><p className="text-xs text-gray-500">Prime Number Analysis</p></div>
            </div>
            <ExportButton data={data?.last_20} filename="w32_prime_analysis" />
          </div>
          {/* Tab bar */}
          <div className="flex gap-1 border-b border-gray-200 -mb-px">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 ${activeTab === 'overview' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              data-testid="tab-overview"
            >
              <Target size={16} /> Prime Overview <span className="text-[10px] font-normal opacity-70">(3 blocks)</span>
            </button>
            <button
              onClick={() => setActiveTab('affinity')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 ${activeTab === 'affinity' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              data-testid="tab-affinity"
            >
              <MagnifyingGlass size={16} /> Prime Affinity <span className="text-[10px] font-normal opacity-70">(co-occurrence)</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W32" {...NARRATIVES.W32} className="mb-5" />
        {activeTab === 'overview' && <OverviewTab data={data} loading={loading} />}
        {activeTab === 'affinity' && <PrimeAffinityTab affinity={affinity} loading={affinityLoading} />}
      </main>
    </div>
  );
}

function OverviewTab({ data, loading }) {
  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded p-4">
          <p className="text-xs uppercase font-bold text-gray-500">Primes in 1-50</p>
          <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="stat-prime-count">{data.prime_count}</p>
        </div>
        <div className="bg-white border rounded p-4 bg-amber-50 border-amber-200">
          <p className="text-xs uppercase font-bold text-amber-600">Actual Prime %</p>
          <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="stat-actual">{data.prime_pct}%</p>
        </div>
        <div className="bg-white border rounded p-4">
          <p className="text-xs uppercase font-bold text-gray-500">Expected %</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{data.expected_pct}%</p>
        </div>
        <div className={`bg-white border rounded p-4 ${data.bias > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <p className="text-xs uppercase font-bold text-gray-500">Bias</p>
          <p className={`text-2xl font-bold mt-1 ${data.bias > 0 ? 'text-green-700' : 'text-red-600'}`}>{data.bias > 0 ? '+' : ''}{data.bias}%</p>
        </div>
      </div>
      <div className="bg-white border rounded p-4 mb-6">
        <h3 className="font-bold text-gray-900 mb-3"><span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block 1</span>Primes in Range 1-50</h3>
        <div className="flex flex-wrap gap-2" data-testid="primes-list">
          {(data.primes_list || []).map(p => (
            <span key={p} className="inline-block w-10 h-10 rounded-full bg-[#002FA7] text-white text-center leading-10 font-mono font-bold">{p}</span>
          ))}
        </div>
      </div>
      <div className="bg-white border rounded p-4 mb-6">
        <h3 className="font-bold text-gray-900 mb-3"><span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block 2</span>Primes per Draw Distribution</h3>
        <div className="space-y-2" data-testid="prime-dist">
          {(data.prime_count_dist || []).map(pc => {
            const max = Math.max(...data.prime_count_dist.map(x => x.draws));
            return (
              <div key={pc.count} className="flex items-center gap-2">
                <span className="font-mono text-sm w-20 text-right font-bold">{pc.count} primes</span>
                <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden relative">
                  <div className={`h-full rounded ${pc.count <= 1 ? 'bg-blue-400' : pc.count <= 2 ? 'bg-[#002FA7]' : 'bg-indigo-700'}`} style={{ width: `${(pc.draws / max) * 100}%` }} />
                  <span className="absolute right-2 top-0.5 text-[10px] font-mono font-bold">{pc.draws}x</span>
                </div>
                <span className="font-mono text-xs w-12 text-right font-bold">{pc.pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 text-white">
          <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl"><span className="inline-block px-2 py-0.5 bg-white/20 text-white rounded mr-2 text-[11px] font-black">Block 3</span>Most Frequent Primes</h3></div>
          <div className="grid grid-cols-3 gap-2" data-testid="top-primes">
            {(data.top_primes || []).map((p, idx) => (
              <div key={p.number} className={`p-2 rounded-lg text-center ${idx < 5 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`} style={idx < 5 ? {borderLeft: '4px solid #2563eb'} : {}}>
                <div className="text-xl font-mono font-bold">{p.number}</div>
                <div className="text-[10px] opacity-80">{p.count}x ({p.pct}%)</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border rounded p-4">
          <h3 className="font-bold text-gray-900 mb-3">Last 20 Draws</h3>
          <div className="overflow-x-auto" data-testid="recent-table">
            <table className="w-full text-xs">
              <thead className="bg-gray-100"><tr>
                <th className="px-2 py-2 text-center">Draw</th>
                <th className="px-2 py-2 text-center">Balls</th>
                <th className="px-2 py-2 text-center">Primes</th>
                <th className="px-2 py-2 text-center">#</th>
              </tr></thead>
              <tbody>
                {(data.last_20 || []).map((d, idx) => (
                  <tr key={d.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-2 py-1 border-b font-mono text-center">#{d.draw}</td>
                    <td className="px-2 py-1 border-b font-mono text-center">{d.balls.join(', ')}</td>
                    <td className="px-2 py-1 border-b text-center">
                      {d.primes_in_draw.map(p => (
                        <span key={p} className="inline-block bg-[#002FA7] text-white font-mono text-[10px] font-bold px-1.5 py-0.5 rounded mr-1">{p}</span>
                      ))}
                    </td>
                    <td className={`px-2 py-1 border-b font-mono text-center font-bold ${d.prime_count >= 3 ? 'text-[#002FA7]' : ''}`}>{d.prime_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function PrimeAffinityTab({ affinity, loading }) {
  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!affinity) return <div className="text-center text-gray-500 py-10">No affinity data available.</div>;
  if (affinity.error) return <div className="bg-red-50 border border-red-200 text-red-700 rounded p-4">{affinity.error}</div>;

  const primeBadge = (n, isPrime) => (
    <span className={`inline-block font-mono font-bold px-1.5 py-0.5 rounded text-[11px] mr-1 ${isPrime ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400' : 'bg-gray-200 text-gray-800'}`}>{n}{isPrime ? '★' : ''}</span>
  );

  const Bar = ({ pct, color }) => (
    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
  );

  return (
    <div className="space-y-4" data-testid="prime-affinity-view">
      {/* Header banner */}
      <div className="bg-gradient-to-br from-indigo-50 to-sky-50 border-2 border-indigo-300 rounded-lg p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-lg text-indigo-900">Prime Affinity — Co-occurrence Finder</h3>
            <p className="text-xs text-indigo-700 mt-1">
              Last draw <b>#{affinity.last_draw_number}</b> ({affinity.last_draw_date}) contained <b>{affinity.last_primes.length} primes</b>: {affinity.last_primes.map(p => primeBadge(p, true))}
            </p>
            <p className="text-[11px] text-indigo-600 mt-1 italic">Finding past draws that contained these primes (individually, in pairs, and all together) and aggregating which OTHER numbers most frequently appeared alongside.</p>
          </div>
          <div className="text-right text-[11px] text-gray-600">
            <div>Scanned: <b>{affinity.total_draws}</b> draws</div>
            <div>Full balls: <b className="font-mono">{(affinity.last_balls || []).join(', ')}</b></div>
          </div>
        </div>
      </div>

      {/* FINAL RESULT — Combined Affinity Score ranking */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-500 rounded-lg overflow-hidden shadow-lg" data-testid="aff-block-result">
        <header className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white px-4 py-3">
          <h3 className="font-black text-base flex items-center gap-2">
            <span className="inline-block px-2 py-0.5 bg-white/20 text-white rounded text-[11px] font-black">FINAL RESULT</span>
            Strongest Affinity Picks — Combined Score Across All Blocks
          </h3>
          <p className="text-[11px] text-white/80 mt-0.5">Total score = Σ individual prime % + Σ pair % + triple % (per number). Higher = more historical co-occurrence signal.</p>
        </header>
        <div className="p-4">
          <div className="text-lg font-black text-emerald-900 mb-3" data-testid="aff-result-verdict">{affinity.result_verdict}</div>
          <table className="w-full text-sm">
            <thead className="bg-emerald-100 text-emerald-900 text-[10px] uppercase">
              <tr>
                <th className="px-2 py-1.5 text-left">Rank</th>
                <th className="px-2 py-1.5 text-center">Number</th>
                <th className="px-2 py-1.5 text-center" title="# blocks where affinity ≥ 10% (primary ranking criterion)">Hits ≥10%</th>
                <th className="px-2 py-1.5 text-right" title="meaningful_hits × 20 + total_score">Composite</th>
                <th className="px-2 py-1.5 text-right">Total %</th>
                <th className="px-2 py-1.5 text-right">Ind. Σ</th>
                <th className="px-2 py-1.5 text-right">Pair Σ</th>
                <th className="px-2 py-1.5 text-right">Triple</th>
                <th className="px-2 py-1.5 text-left">Meaningful Blocks (≥10%)</th>
              </tr>
            </thead>
            <tbody>
              {(affinity.combined_ranked || []).slice(0, 12).map((r, idx) => {
                const isTop5 = idx < 5;
                return (
                  <tr key={r.number} className={`border-b border-emerald-100 ${isTop5 ? 'bg-emerald-50' : ''}`} data-testid={`aff-combined-${r.number}`}>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-black ${idx === 0 ? 'bg-amber-500 text-white' : idx === 1 ? 'bg-gray-400 text-white' : idx === 2 ? 'bg-orange-700 text-white' : 'bg-gray-200 text-gray-700'}`}>#{idx + 1}</span>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`font-mono font-black text-xl ${r.is_prime ? 'text-amber-800' : 'text-emerald-800'}`}>{r.number}{r.is_prime ? '★' : ''}</span>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`font-mono font-black px-2 py-0.5 rounded ${r.meaningful_hits >= 5 ? 'bg-emerald-600 text-white' : r.meaningful_hits >= 3 ? 'bg-blue-500 text-white' : r.meaningful_hits >= 2 ? 'bg-amber-400 text-white' : 'bg-gray-200 text-gray-600'}`}>{r.meaningful_hits}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <span className={`font-mono font-black ${isTop5 ? 'text-emerald-700 text-base' : 'text-gray-600'}`}>{r.composite_score}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-gray-700">{r.total_score}%</td>
                    <td className="px-2 py-1.5 text-right font-mono text-amber-700">{r.individual_sum}%</td>
                    <td className="px-2 py-1.5 text-right font-mono text-sky-700">{r.pair_sum}%</td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{r.triple_pct > 0 ? `${r.triple_pct}%` : '—'}</td>
                    <td className="px-2 py-1.5 text-[10px] text-gray-600 font-mono">
                      {(r.meaningful_blocks || []).length > 0 ? (r.meaningful_blocks || []).join(' · ') : <span className="italic text-gray-400">none ≥10%</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Block 1 — Individual prime affinity */}
      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="aff-block-individual">
        <header className="bg-slate-800 text-white px-4 py-2.5">
          <h3 className="font-bold text-sm"><span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block 1</span>Individual Prime Affinity</h3>
          <p className="text-[11px] text-white/70 mt-0.5">For each prime from the last draw, top-10 numbers that historically appeared alongside it. ★ = companion is also a prime.</p>
        </header>
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(affinity.individual || {}).map(([p, aff]) => (
            <div key={p} className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid={`aff-individual-${p}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <span className="text-xs text-gray-500 uppercase font-bold">Prime</span>
                  <div className="text-3xl font-mono font-black text-amber-700">{p}★</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-500 uppercase">Appeared in</div>
                  <div className="text-lg font-mono font-bold text-gray-800">{aff.match_count}</div>
                  <div className="text-[10px] text-gray-400">past draws</div>
                </div>
              </div>
              <div className="space-y-1">
                {(aff.companions || []).slice(0, 10).map(c => (
                  <div key={c.number} className="flex items-center gap-2 text-[11px]">
                    <span className={`font-mono font-bold w-7 text-right ${c.is_prime ? 'text-amber-800' : 'text-gray-700'}`}>{c.number}{c.is_prime ? '★' : ''}</span>
                    <Bar pct={c.pct * 3} color={c.is_prime ? 'bg-amber-500' : 'bg-indigo-500'} />
                    <span className="font-mono font-bold w-14 text-right text-gray-700">{c.pct}% <span className="text-gray-400">({c.count})</span></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Block 2 — Pairs */}
      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="aff-block-pairs">
        <header className="bg-slate-800 text-white px-4 py-2.5">
          <h3 className="font-bold text-sm"><span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block 2</span>Prime Pair Affinity</h3>
          <p className="text-[11px] text-white/70 mt-0.5">Past draws containing BOTH primes in each pair, and which third/fourth/fifth numbers appeared alongside.</p>
        </header>
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {(affinity.pairs || []).map((pair, idx) => (
            <div key={idx} className="bg-sky-50 border border-sky-300 rounded-lg p-3" data-testid={`aff-pair-${idx}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-xs text-gray-500 uppercase font-bold mr-1">Pair</span>
                  {pair.pair.map(p => <span key={p} className="inline-block bg-amber-200 text-amber-900 ring-1 ring-amber-400 font-mono font-black text-lg px-2 py-0.5 rounded">{p}★</span>)}
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-500 uppercase">Together in</div>
                  <div className="text-lg font-mono font-bold text-sky-800">{pair.match_count}</div>
                  <div className="text-[10px] text-gray-400">past draws</div>
                </div>
              </div>
              {pair.match_count === 0 ? (
                <div className="text-xs text-gray-500 italic py-2">No past draws contain both primes together.</div>
              ) : (
                <div className="space-y-1">
                  {(pair.companions || []).slice(0, 8).map(c => (
                    <div key={c.number} className="flex items-center gap-2 text-[11px]">
                      <span className={`font-mono font-bold w-7 text-right ${c.is_prime ? 'text-amber-800' : 'text-gray-700'}`}>{c.number}{c.is_prime ? '★' : ''}</span>
                      <Bar pct={c.pct * 2} color={c.is_prime ? 'bg-amber-500' : 'bg-sky-500'} />
                      <span className="font-mono font-bold w-14 text-right text-gray-700">{c.pct}% <span className="text-gray-400">({c.count})</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Block 3 — Triple / All primes together */}
      {affinity.triple && (
        <section className="bg-white border-2 border-emerald-300 rounded-lg overflow-hidden" data-testid="aff-block-triple">
          <header className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white px-4 py-2.5">
            <h3 className="font-bold text-sm"><span className="inline-block px-2 py-0.5 bg-white/20 rounded mr-2 text-[11px] font-black">Block 3</span>All Primes Together (Triple)</h3>
            <p className="text-[11px] text-white/80 mt-0.5">Rare — past draws that contained ALL primes from the last draw. Any non-prime appearing here is a high-confluence pick.</p>
          </header>
          <div className="p-4">
            {(() => {
              const t = affinity.triple.primary ? affinity.triple.primary : affinity.triple;
              return (
                <>
                  <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs text-gray-500 uppercase font-bold mr-1">Triple</span>
                      {t.triple.map(p => <span key={p} className="inline-block bg-amber-200 text-amber-900 ring-2 ring-amber-500 font-mono font-black text-xl px-2.5 py-1 rounded">{p}★</span>)}
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-gray-500 uppercase">Found together in</div>
                      <div className="text-3xl font-mono font-black text-emerald-700">{t.match_count}</div>
                      <div className="text-[10px] text-gray-400">past draws</div>
                    </div>
                  </div>
                  {t.match_count === 0 ? (
                    <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded">These three primes have NEVER co-occurred historically. This pattern is unprecedented.</div>
                  ) : (
                    <>
                      <div className="text-[11px] font-bold text-emerald-900 uppercase mb-2">Companion numbers:</div>
                      <div className="space-y-1 mb-3">
                        {(t.companions || []).slice(0, 12).map(c => (
                          <div key={c.number} className="flex items-center gap-2 text-xs">
                            <span className={`font-mono font-bold w-8 text-right text-base ${c.is_prime ? 'text-amber-800' : 'text-emerald-800'}`}>{c.number}{c.is_prime ? '★' : ''}</span>
                            <Bar pct={c.pct} color={c.is_prime ? 'bg-amber-500' : 'bg-emerald-500'} />
                            <span className="font-mono font-bold w-16 text-right text-gray-700">{c.pct}% <span className="text-gray-400">({c.count}/{t.match_count})</span></span>
                          </div>
                        ))}
                      </div>
                      {(t.sample_draws || []).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="text-[11px] font-bold text-gray-600 uppercase mb-1">Sample matching draws (most recent):</div>
                          <div className="space-y-0.5 text-[11px] font-mono">
                            {t.sample_draws.map(d => (
                              <div key={d.draw_number} className="text-gray-600">
                                #{d.draw_number} ({d.draw_date}): {d.balls.join(', ')} <span className="text-gray-400">→ companions: {d.companions.join(', ')}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </section>
      )}

      {/* Block 4 (Consensus) removed — superseded by FINAL RESULT panel at top */}
    </div>
  );
}
